import React, { useState, useEffect } from "react";
import {
  TextField,
  Chip,
  Box,
  FormHelperText,
} from "@mui/material";
import { NotificationManager } from "react-notifications";


export default function MultiEmailInput({
  value = "",
  onChange,
  label = "Email Addresses",
  placeholder = "Enter email and press Enter or semicolon",
  helperText = "Emails separated by semicolon",
  error = false,
  disabled = false,
  required = false,
  fullWidth = true,
  size = "small",
}) {
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  // Initialize emails from value prop and update when value changes
  useEffect(() => {
    if (value) {
      const rawEmails = value.split(";").map((e) => e.trim());
      const validEmails = [];
      let hasInvalid = false;

      rawEmails.forEach((email) => {
        if (!email) return;

        if (isValidEmail(email)) {
          validEmails.push(email);
        } else {
          hasInvalid = true;
          setInputError("Invalid email format found");
          NotificationManager.error(`Invalid email: ${email}`);
        }
      });

      setEmails(validEmails);
    } else {
      setEmails([]);
    }
  }, [value]);


  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)*\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  // Handle adding email
  const addEmail = (emailToAdd) => {
    const trimmedEmail = emailToAdd.trim();

    if (!trimmedEmail) {
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setInputError("Please enter a valid email address");
      NotificationManager.error("Invalid email format");
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setInputError("This email is already added");
      NotificationManager.error("This email is already added");
      return;
    }

    const newEmails = [...emails, trimmedEmail];
    setEmails(newEmails);
    setInputValue("");
    setInputError("");

    NotificationManager.success("Email added");

    if (onChange) {
      const emailString = newEmails.join(";");
      const syntheticEvent = {
        target: {
          value: emailString,
          name: label
        }
      };
      onChange(syntheticEvent);
    }
  };


  // Handle removing email
  const removeEmail = (emailToRemove) => {
    const newEmails = emails.filter((email) => email !== emailToRemove);
    setEmails(newEmails);

    // Call onChange with semicolon-separated string
    if (onChange) {
      const emailString = newEmails.join(";");
      const syntheticEvent = {
        target: {
          value: emailString,
          name: label
        }
      };
      onChange(syntheticEvent);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputError("");

    // Check if semicolon is entered
    if (value.includes(";")) {
      const emailParts = value.split(";");
      // Add all complete emails
      emailParts.slice(0, -1).forEach((email) => {
        if (email.trim()) {
          addEmail(email);
        }
      });
      // Keep the last part as input
      setInputValue(emailParts[emailParts.length - 1]);
    } else {
      setInputValue(value);
    }
  };

  // Handle key down events
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ";") {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
      // Remove last email when backspace is pressed on empty input
      removeEmail(emails[emails.length - 1]);
    }
  };

  // Handle blur event - add email if valid
  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  return (
    <Box>
      <TextField
        label={label}
        variant="outlined"
        fullWidth={fullWidth}
        size={size}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={emails.length === 0 ? placeholder : ""}
        error={error || !!inputError}
        disabled={disabled}
        required={required && emails.length === 0}
        InputProps={{
          startAdornment: (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mr: 1 }}>
              {emails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => removeEmail(email)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  disabled={disabled}
                />
              ))}
            </Box>
          ),
        }}
      />
      {(inputError || helperText) && (
        <FormHelperText error={!!inputError}>
          {inputError || helperText}
        </FormHelperText>
      )}
    </Box>
  );
}